import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Bookmark } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "../../auth/AuthContext";
import { fetchSavedPosts, unsavePost } from "../../api/leaks";
import LeakTable from "../../components/LeakTable";
import { FeedFilters } from "../../components/feed-filters/FeedFilters";
import type { FilterState } from "../../components/feed-filters/FeedFilters";
import { toast } from "sonner";

export default function SavedPosts() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [allSaved, setAllSaved] = useState<any[]>([]);
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  
  const [filters, setFilters] = useState<FilterState>({});

  useEffect(() => {
    fetchSavedPosts().then(res => {
      setAllSaved(res.results || []);
    }).catch(err => {
      console.error("Failed to fetch saved posts", err);
      setAllSaved([]);
    }).finally(() => setLoading(false));
  }, []);

  const handleToggleSave = async (leak: any) => {
    try {
      await unsavePost(leak._id);
      setSavedIds(prev => {
        const next = new Set(prev);
        next.delete(leak._id);
        return next;
      });
      setAllSaved(prev => prev.filter(p => p._id !== leak._id));
      toast.success("Post removed from saved");
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Failed to remove post");
    }
  };

  const filteredPosts = useMemo(() => {
    return allSaved.filter(post => {
      if (filters.category) {
        // Just simple logic since we're in frontend, but better would be exact match with backend definition.
        // Actually, the API doesn't filter saved posts by category directly unless we build a query in backend. 
        // We'll just do simple string matching for now.
        const isRansomware = post.forum && ['blog','locker','team','cartel','group','force','interlock','lynx','qilin','cipher','genesis','insomnia','chaos','abyss','payload','pear','everest','daixin','radar','embargo','sarcoma','spire','bear','termite','lockbit','ailock','nova','bashe'].some(p => post.forum.toLowerCase().includes(p));
        const cats = filters.category.split(",");
        const matchRans = cats.includes("Ransomware sites") && isRansomware;
        const matchDark = cats.includes("Darknet sites") && !isRansomware;
        if (!matchRans && !matchDark) return false;
      }
      if (filters.threatActor) {
        const actors = filters.threatActor.split(",");
        if (!actors.includes(post.forum)) return false;
      }
      if (filters.targetType && filters.targetValue) {
        const vals = filters.targetValue.split(",");
        const t = filters.targetType.toLowerCase();
        let match = false;
        if (t === "country") match = vals.includes(post.country);
        else if (t === "industry") match = vals.includes(post.industry);
        else if (t === "organization") match = vals.includes(post.company_name);
        else if (t === "site") match = vals.includes(post.site) || vals.includes(post.company_domain);
        else if (t === "region") match = vals.includes(post.region);
        if (!match) return false;
      }
      if (filters.sector && post.sectors !== filters.sector) return false;
      if (filters.network && post.network !== filters.network) return false;
      if (filters.severity) {
        const isCritical = post.data_types?.some((x:string) => ["Credentials", "Financial", "PII"].includes(x));
        const isHigh = post.data_types?.some((x:string) => ["Personal Data", "Medical"].includes(x));
        const sev = isCritical ? "Critical" : isHigh ? "High" : "Low";
        if (sev !== filters.severity) return false;
      }
      return true;
    });
  }, [allSaved, filters]);

  return (
    <div className="leaks-page saved-posts-page">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="leaks-inner">
        <Link to="/leaks" className="inline-flex items-center gap-2 text-sm text-indigo-400 hover:text-indigo-300 mb-6 transition-colors">
          <ArrowLeft size={16} /> Back to feed
        </Link>
        
        <div className="rounded-2xl p-8 mb-8 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(0,102,224,0.1) 0%, rgba(79,70,229,0.05) 100%)' }}>
          <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-20"></div>
          <div className="relative z-10">
            <h1 className="text-3xl font-semibold text-white tracking-tight mb-2">Saved Posts</h1>
            <p className="text-slate-400 max-w-xl mb-4">
              Access your bookmarked threat intelligence and leak reports for quick reference.
            </p>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 border border-slate-700 text-sm">
              <span className="text-white font-medium">{allSaved.length}</span>
              <span className="text-slate-400">/ 200 posts saved</span>
            </div>
          </div>
        </div>

        <FeedFilters 
          filters={filters} 
          onChange={setFilters} 
          onClear={() => setFilters({})}
          hideSearch={true}
        />

        {loading ? (
          <div className="animate-pulse space-y-2 mt-8">
            {[...Array(3)].map((_, i) => <div key={i} className="h-[72px] bg-slate-800/30 rounded-lg"></div>)}
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-24 border border-dashed border-slate-700/50 rounded-xl mt-8">
            <Bookmark className="mx-auto text-slate-600 mb-4" size={32} />
            <h3 className="text-lg text-white font-medium mb-1">No saved posts</h3>
            <p className="text-sm text-slate-400">You haven't bookmarked any posts yet.</p>
          </div>
        ) : (
          <div className="mt-8">
            <LeakTable
              leaks={filteredPosts}
              isAdmin={user?.role === "admin"}
              onRowClick={(leak) => navigate(`/leaks/${leak._id}`)}
            />
          </div>
        )}
      </motion.div>
    </div>
  );
}
